import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { getAnonymousUserContext } from "@src/appV2/FeatureFlags/cbhUserContext";
import { getAppInfo, isCapacitorPlatform } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { getDeviceId } from "@src/appV2/NativeAdapters/device.adapter";
import { USER_EVENTS } from "@src/constants";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Deploy } from "cordova-plugin-ionic";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CheckForUpdateResponse } from "cordova-plugin-ionic/dist/IonicCordova";
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import { configureDeploy } from "./utils";
import { useSession } from "../store/helperHooks";

function getOtaDownloadMetrics(extractionStartTime: Date, startTime: Date, endTime: Date) {
  const downloadOnlyTimeInSeconds = (extractionStartTime.getTime() - startTime.getTime()) / 1000;
  const extractionTimeInSeconds = (endTime.getTime() - extractionStartTime.getTime()) / 1000;
  const totalTimeInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
  return {
    downloadOnlyTimeInSeconds,
    extractionTimeInSeconds,
    totalTimeInSeconds,
  };
}

type OtaCheckStatus = "not-started" | "in-progress" | "done";

export function useOtaUpdate() {
  const [otaUpdateInfo, setOtaUpdateInfo] = useState<CheckForUpdateResponse>();
  const [otaUpdateProgress, setOtaUpdateProgress] = useState(0);
  const { isAuthorized: isUserAuthenticated } = useSession();

  const otaCheckStatusRef = useRef<OtaCheckStatus>("not-started");

  const ldClient = useLDClient();

  const checkForOtaUpdate = useCallback(async () => {
    if (otaCheckStatusRef.current !== "not-started") {
      // Prevent concurrent OTA downloads
      return;
    }

    otaCheckStatusRef.current = "in-progress";

    try {
      if (!isUserAuthenticated) {
        if (!isDefined(ldClient)) {
          // Allow retry once ldClient loads
          otaCheckStatusRef.current = "not-started";
          return;
        }

        const { identifier: deviceId } = await getDeviceId();
        const appInfo = await getAppInfo();
        const anonymousContext = getAnonymousUserContext(appInfo, deviceId);
        await ldClient.identify(anonymousContext);
        const isOtaEnabledForUnauthenticatedUsers = Boolean(
          ldClient?.variation(CbhFeatureFlag.ALLOW_OTA_FOR_UNAUTHENTICATED_USERS, false)
        );

        if (!isOtaEnabledForUnauthenticatedUsers) {
          // Allow retry once user is authenticated
          otaCheckStatusRef.current = "not-started";
          return;
        }
      }

      await configureDeploy();
      logEvent(USER_EVENTS.OTA_UPDATE_CHECKING_FOR_UPDATE);
      const updateInfo = await Deploy.checkForUpdate();

      if (!updateInfo?.available) {
        logEvent(USER_EVENTS.OTA_UPDATE_NOT_AVAILABLE);
      } else {
        logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_STARTED, {
          buildId: updateInfo.build,
        });

        const startTime = new Date();
        await Deploy.downloadUpdate((progress) => {
          if (progress && progress % 5 === 0) {
            setOtaUpdateProgress(progress);
          }
        });

        const extractionStartTime = new Date();
        await Deploy.extractUpdate();
        const endTime = new Date();
        const { downloadOnlyTimeInSeconds, extractionTimeInSeconds, totalTimeInSeconds } =
          getOtaDownloadMetrics(extractionStartTime, startTime, endTime);

        logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOADED, {
          buildId: updateInfo.build,
          // @deprecated. Used for metrics right now, but should move to `totalTimeTakenInSeconds` for newer metrics
          downloadTimeSeconds: totalTimeInSeconds,
          downloadOnlyTimeInSeconds,
          extractionTimeInSeconds,
          totalTimeInSeconds,
        });

        setOtaUpdateInfo(updateInfo);
      }
    } catch (error) {
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_ERROR, { metadata: { error } });
    }

    otaCheckStatusRef.current = "done";
  }, [isUserAuthenticated, ldClient]);

  useEffect(() => {
    if (isCapacitorPlatform()) {
      checkForOtaUpdate();
    }
  }, [checkForOtaUpdate]);

  return {
    otaUpdateInfo,
    otaUpdateProgress,
  };
}
